/**
 * Gestion des licences (SUPER_ADMIN).
 * Chiffres clés, abonnements du mois, licences bientôt expirées, tableau des licences,
 * actions (suspendre, réactiver, renouveler, révoquer) et facture PDF.
 */
import { Aide } from '@/components/ui/aide';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Key,
  DollarSign,
  Plus,
  Edit,
  PauseCircle,
  PlayCircle,
  Ban,
  RotateCcw,
  Download,
  Eye,
  Search,
  X,
  Building2,
  Users,
  Truck,
} from 'lucide-react';
import { licensesAPI, organizationsAPI } from '../lib/api';
import { organizationOption } from '../lib/selectOptions';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { SearchableSelect } from '../components/ui/searchable-select';
import { MODULE_GROUPS, RETIRED_MODULES, moduleLabel } from '../lib/modules';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import { StatutBadge } from '../components/ui/StatutBadge';
import { libelle, nomLisible, optionsLibelles } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatDateHeureOuTiret, formatDateOuTiret, formatMontant, formatNombre } from '../lib/formats';

const PLANS = [
  'TRANSPORT_DISTRIBUTION',
  'STATIONS_RESEAU',
  'CONTROLE_CARBURANT',
];
const LEGACY_PLANS = ['ENTERPRISE'];
const FILTER_PLANS = [...PLANS, ...LEGACY_PLANS];
// Nom d'une formule: les anciennes formules sont signalées comme telles.
const planNom = (plan) =>
  LEGACY_PLANS.includes(plan)
    ? `${libelle('licensePlan', plan)} (ancienne formule)`
    : libelle('licensePlan', plan);
const PLAN_DESCRIPTION = {
  TRANSPORT_DISTRIBUTION: 'Commandes, camions, trajets, documents, suivi des camions, factures et comparaison des volumes.',
  STATIONS_RESEAU: 'Stations, cuves, pompes, caisse, équipes, achats, ventes et rapports de station.',
  CONTROLE_CARBURANT: 'Sites, engins, demandes, sorties de carburant, plafonds et consommation interne.',
};
const PLAN_DEFAULTS = {
  TRANSPORT_DISTRIBUTION: { max_users: 25, max_trucks: 50 },
  STATIONS_RESEAU: { max_users: 50, max_trucks: 0 },
  CONTROLE_CARBURANT: { max_users: 25, max_trucks: 10 },
  ENTERPRISE: { max_users: 500, max_trucks: 1000 },
};
const PLAN_PRICES_MONTHLY_XOF = {
  TRANSPORT_DISTRIBUTION: 200000,
  STATIONS_RESEAU: 200000,
  CONTROLE_CARBURANT: 200000,
  ENTERPRISE: 750000,
};
const PLAN_COLOR = {
  TRANSPORT_DISTRIBUTION: { bg: 'bg-info/15', text: 'text-info', ring: 'ring-info/40' },
  STATIONS_RESEAU: { bg: 'bg-success/15', text: 'text-success', ring: 'ring-success/40' },
  CONTROLE_CARBURANT: { bg: 'bg-flame/15', text: 'text-flame-ink', ring: 'ring-flame/40' },
  ENTERPRISE: { bg: 'bg-process/15', text: 'text-process', ring: 'ring-process/40' },
};
// Le pack Transport et distribution inclut les modules station (programme
// Stations, décision du 28 octobre 2026) : la liste est écrite en entier, car
// `scripts/validate_module_catalogs.py` la relit telle quelle.
const DEFAULT_MODULES = {
  TRANSPORT_DISTRIBUTION: [
    'trucks', 'drivers', 'maintenance', 'truck_rentals', 'trips', 'tracking', 'gps_tracking', 'orders', 'clients',
    'dispatch_allocations', 'invoices', 'payments', 'stock_movements', 'documents',
    'workflow_engine', 'volume_reconciliation', 'offline_sync', 'reports', 'alerts',
    'pricing_rules', 'finance_cockpit', 'operations', 'companies', 'advanced_audit',
    'external_integrations', 'lots',
    'stations', 'tanks', 'pumps', 'prices', 'purchases', 'station_sales', 'station_pos',
    'shifts', 'attendants', 'station_expenses', 'station_kpis', 'station_daily_report',
    'multi_station_dashboard', 'station_credit', 'station_annexes',
  ],
  STATIONS_RESEAU: [
    'stations', 'tanks', 'pumps', 'prices', 'pricing_rules', 'purchases', 'station_sales', 'station_pos',
    'shifts', 'attendants', 'station_expenses', 'stock_movements', 'station_kpis',
    'station_daily_report', 'multi_station_dashboard', 'station_credit', 'station_annexes',
    'reports', 'documents', 'alerts', 'finance_cockpit', 'offline_sync', 'volume_reconciliation',
  ],
  CONTROLE_CARBURANT: [
    'sites', 'assets', 'fuel_requests',
    'fuel_issues', 'fuel_quotas', 'fuel_consumption', 'stock_movements',
    'volume_reconciliation', 'documents', 'alerts', 'reports', 'offline_sync',
  ],
};
DEFAULT_MODULES.ENTERPRISE = Array.from(new Set([
  ...DEFAULT_MODULES.TRANSPORT_DISTRIBUTION,
  ...DEFAULT_MODULES.STATIONS_RESEAU,
  ...DEFAULT_MODULES.CONTROLE_CARBURANT,
  'advanced_audit',
  'external_integrations',
])).sort();

const fmtXOF = (n) => formatMontant(n ?? 0);
const fmtDate = formatDateOuTiret;
const fmtDateTime = formatDateHeureOuTiret;

const planColor = (plan) => PLAN_COLOR[plan] || PLAN_COLOR.TRANSPORT_DISTRIBUTION;
const defaultMonthlyPrice = (plan) => PLAN_PRICES_MONTHLY_XOF?.[plan] || 200000;

export default function LicensesPage() {
  const [searchParams] = useSearchParams();
  const [licenses, setLicenses] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [mrr, setMrr] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showCreate, setShowCreate] = useState(searchParams.get('new') === '1');
  const [editing, setEditing] = useState(null);
  const [detailLicense, setDetailLicense] = useState(null);
  const [detailAudit, setDetailAudit] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lic, o, m, ex] = await Promise.allSettled([
        licensesAPI.getEvery(),
        organizationsAPI.getEvery(),
        licensesAPI.getMRR(),
        licensesAPI.getExpiringSoon(30),
      ]);
      if (lic.status === 'fulfilled') setLicenses(lic.value || []);
      if (o.status === 'fulfilled') setOrgs(o.value || []);
      if (m.status === 'fulfilled') setMrr(m.value);
      if (ex.status === 'fulfilled') setExpiring(ex.value || []);
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger les licences'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const orgMap = useMemo(() => {
    const map = {};
    (orgs || []).forEach((o) => { map[o.id] = o; });
    return map;
  }, [orgs]);

  const stats = useMemo(() => {
    const total = licenses.length;
    const active = licenses.filter((l) => l.status === 'ACTIVE').length;
    const suspended = licenses.filter((l) => l.status === 'SUSPENDED').length;
    const expired = licenses.filter((l) => l.status === 'EXPIRED' || l.status === 'REVOKED').length;
    return { total, active, suspended, expired };
  }, [licenses]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return licenses.filter((l) => {
      const org = orgMap[l.organization_id];
      const matchesSearch = !q
        || (org?.name && org.name.toLowerCase().includes(q))
        || (l.notes && l.notes.toLowerCase().includes(q));
      const matchesPlan = planFilter === 'all' || l.plan === planFilter;
      const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
      return matchesSearch && matchesPlan && matchesStatus;
    });
  }, [licenses, orgMap, search, planFilter, statusFilter]);
  const pagination = usePagination(filtered, { reinitialiser: `${search}|${planFilter}|${statusFilter}` });

  const openDetail = async (lic) => {
    setDetailLicense(lic);
    try {
      const audit = await licensesAPI.getAudit(lic.id);
      setDetailAudit(audit || []);
    } catch {
      setDetailAudit([]);
    }
  };

  const handleSuspend = async (lic) => {
    const reason = prompt('Raison de la suspension ?') || undefined;
    try {
      await licensesAPI.suspend(lic.id, reason);
      toast.success('Licence suspendue');
      loadAll();
    } catch (e) { toast.error(messageErreur(e, 'Impossible de suspendre la licence')); }
  };

  const handleReactivate = async (lic) => {
    try {
      await licensesAPI.reactivate(lic.id);
      toast.success('Licence réactivée');
      loadAll();
    } catch (e) { toast.error(messageErreur(e, 'Impossible de réactiver la licence')); }
  };

  const handleRevoke = async (lic) => {
    if (!window.confirm('Révoquer définitivement cette licence ?')) return;
    try {
      await licensesAPI.revoke(lic.id);
      toast.success('Licence révoquée');
      loadAll();
    } catch (e) { toast.error(messageErreur(e, 'Impossible de révoquer la licence')); }
  };

  const handleRenew = async (lic) => {
    const months = parseInt(prompt('Durée de renouvellement (mois) ?', '12'), 10);
    if (!months || months < 1) return;
    const expDate = new Date();
    expDate.setMonth(expDate.getMonth() + months);
    try {
      await licensesAPI.renew(lic.id, { expires_at: expDate.toISOString(), notes: `Renouvelé ${months} mois` });
      toast.success('Licence renouvelée');
      loadAll();
    } catch (e) { toast.error(messageErreur(e, 'Impossible de renouveler la licence')); }
  };

  const handleInvoice = async (lic, months = 12) => {
    try {
      const blob = await licensesAPI.downloadInvoice(lic.id, months);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const nomClient = nomLisible(orgMap[lic.organization_id]?.name, 'client')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      a.download = `facture-licence-${nomClient || 'client'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (e) { toast.error(messageErreur(e, 'Impossible de télécharger la facture')); }
  };

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Licences"
        description="Les abonnements de chaque organisation: formule, limites, date d’expiration et facture."
        stats={[
          { label: 'Actives', valeur: stats.active, ton: 'positif' },
          { label: 'Suspendues', valeur: stats.suspended, ton: stats.suspended ? 'attention' : 'neutre' },
          { label: 'Expirées ou révoquées', valeur: stats.expired, ton: stats.expired ? 'critique' : 'neutre' },
          {
            label: 'Abonnements par mois (estimé)',
            valeur: mrr ? mrr.total_monthly : null,
            unite: mrr ? 'FCFA' : undefined,
            aide: mrr ? `Par an : ${fmtXOF(mrr.total_annual)}` : undefined,
          },
        ]}
        statsPlus={
          <p className="text-sm">
            <span className="text-muted-foreground">Total des licences : </span>
            <span className="font-medium tabular-nums">{stats.total}</span>
          </p>
        }
        alertesSousTitre
        alertes={
          expiring && expiring.length > 0
            ? [
                {
                  niveau: 'attention',
                  nombre: expiring.length,
                  court: expiring.length > 1 ? 'licences bientôt expirées' : 'licence bientôt expirée',
                  texte: `Licences bientôt expirées : ${expiring
                    .slice(0, 6)
                    .map((l) => `${nomLisible(l.organization_name, '—')} (${libelle('licensePlan', l.plan)}, expire le ${fmtDate(l.expires_at)})`)
                    .join(' ; ')}${expiring.length > 6 ? ` et ${expiring.length - 6} autres` : ''}.`,
                  action: {
                    label: `Ouvrir ${nomLisible(expiring[0].organization_name, 'la licence')}`,
                    onClick: () => openDetail(expiring[0]),
                  },
                },
              ]
            : undefined
        }
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary px-4">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle licence
          </button>
        }
      />

      {/* Répartition par formule */}
      {mrr?.by_plan && (
        <div className="grid gap-3 md:grid-cols-3">
          {Object.entries(mrr.by_plan).map(([plan, v]) => (
            <div key={plan} className={`rounded-2xl border ring-1 ${planColor(plan).ring} ${planColor(plan).bg} p-3`}>
              <div className="flex items-center justify-between">
                <div className={`text-xs font-black uppercase tracking-widest ${planColor(plan).text}`}>{planNom(plan)}</div>
                <Key className={`h-4 w-4 ${planColor(plan).text}`} />
              </div>
              <div className={`kpi-value mt-1 ${planColor(plan).text}`}>{formatNombre(v.count)}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {fmtXOF(v.price_monthly)} par mois · {fmtXOF(v.subtotal_monthly)} au total
              </div>
            </div>
          ))}
        </div>
      )}


      {/* Filters */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-muted-foreground/70" />
            <input
              type="text"
              placeholder="Rechercher une organisation ou une note…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md rounded-md border border-border px-3 py-2 text-sm"
            />
          </div>
          <select
            aria-label="Formule"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="rounded-md border border-border px-3 py-2 text-sm"
          >
            <option value="all">Toutes les formules</option>
            {FILTER_PLANS.map((p) => <option key={p} value={p}>{planNom(p)}</option>)}
          </select>
          <select
            aria-label="État"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-border px-3 py-2 text-sm"
          >
            <option value="all">Tous les états</option>
            {optionsLibelles('licenseStatus', ['ACTIVE', 'SUSPENDED', 'EXPIRED', 'REVOKED']).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left readout-label">
              <tr>
                <th className="px-4 py-3">Organisation</th>
                <th className="px-4 py-3">Formule</th>
                <th className="px-4 py-3">État</th>
                <th className="px-4 py-3">Limites (utilisateurs, camions)</th>
                <th className="px-4 py-3">Créée le</th>
                <th className="px-4 py-3">Expire le</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground/70">Chargement…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground/70">Aucune licence pour le moment</td></tr>
              )}
              {pagination.lignes.map((l) => {
                const org = orgMap[l.organization_id];
                return (
                  <tr key={l.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <span className="font-medium text-muted-foreground">{nomLisible(org?.name, '—')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${planColor(l.plan).bg} ${planColor(l.plan).text}`}>
                        {planNom(l.plan)}
                      </span>
                      <div className="mt-1 text-[10px] font-medium text-muted-foreground/70">
                        {(l.enabled_modules || DEFAULT_MODULES[l.plan] || []).length} accès cochés
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatutBadge domaine="licenseStatus" code={l.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" aria-hidden="true" /> {formatNombre(l.max_users)}
                        <Truck className="ml-2 h-3 w-3" aria-hidden="true" /> {formatNombre(l.max_trucks)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(l.expires_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <IconBtn title="Voir le détail" onClick={() => openDetail(l)}><Eye className="h-4 w-4" /></IconBtn>
                        <IconBtn title="Télécharger la facture (PDF)" onClick={() => handleInvoice(l, 12)}><Download className="h-4 w-4 text-process" /></IconBtn>
                        <IconBtn title="Modifier" onClick={() => setEditing(l)}><Edit className="h-4 w-4" /></IconBtn>
                        {l.status === 'ACTIVE' && (
                          <>
                            <IconBtn title="Renouveler" onClick={() => handleRenew(l)}><RotateCcw className="h-4 w-4 text-info" /></IconBtn>
                            <IconBtn title="Suspendre" onClick={() => handleSuspend(l)}><PauseCircle className="h-4 w-4 text-flame-ink" /></IconBtn>
                          </>
                        )}
                        {l.status === 'SUSPENDED' && (
                          <IconBtn title="Réactiver" onClick={() => handleReactivate(l)}><PlayCircle className="h-4 w-4 text-success" /></IconBtn>
                        )}
                        {l.status !== 'REVOKED' && (
                          <IconBtn title="Révoquer" onClick={() => handleRevoke(l)}><Ban className="h-4 w-4 text-destructive" /></IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {!loading && filtered.length > 0 ? <BarrePagination pagination={pagination} nom="licences" className="mt-0" /> : null}

      {/* Fenêtre de création et de modification */}
      {(showCreate || editing) && (
        <LicenseFormModal
          license={editing}
          orgs={orgs}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); loadAll(); }}
        />
      )}

      {/* Fenêtre de détail */}
      {detailLicense && (
        <LicenseDetailModal
          license={detailLicense}
          org={orgMap[detailLicense.organization_id]}
          audit={detailAudit}
          onClose={() => { setDetailLicense(null); setDetailAudit([]); }}
          onInvoice={(months) => handleInvoice(detailLicense, months)}
        />
      )}
    </div>
  );
}


const IconBtn = ({ children, onClick, title }) => (
  <button
    onClick={onClick}
    title={title}
    className="rounded-md p-1.5 hover:bg-muted"
  >
    {children}
  </button>
);

function LicenseFormModal({ license, orgs, onClose, onSaved }) {
  const initialPlan = PLANS.includes(license?.plan) ? license.plan : 'TRANSPORT_DISTRIBUTION';
  const isLegacyEdit = Boolean(license?.plan && !PLANS.includes(license.plan));
  const [form, setForm] = useState(() => ({
    organization_id: license?.organization_id || (orgs[0]?.id || ''),
    plan: initialPlan,
    max_users: license?.max_users || PLAN_DEFAULTS[initialPlan].max_users,
    max_trucks: license?.max_trucks ?? PLAN_DEFAULTS[initialPlan].max_trucks,
    enabled_modules: license?.enabled_modules || DEFAULT_MODULES[initialPlan] || [],
    billing_period: license?.billing_period || 'monthly',
    price_amount_xof: license?.price_amount_xof ?? license?.price_monthly_xof ?? defaultMonthlyPrice(initialPlan),
    expires_at: license?.expires_at?.slice(0, 10) || '',
    notes: license?.notes || '',
  }));

  const monthlyEquivalent = form.billing_period === 'annual'
    ? Number(form.price_amount_xof || 0) / 12
    : Number(form.price_amount_xof || 0);
  const annualEquivalent = form.billing_period === 'annual'
    ? Number(form.price_amount_xof || 0)
    : Number(form.price_amount_xof || 0) * 12;

  const onPlanChange = (plan) => {
    setForm((f) => ({
      ...f,
      plan,
      max_users: PLAN_DEFAULTS[plan].max_users,
      max_trucks: PLAN_DEFAULTS[plan].max_trucks,
      enabled_modules: DEFAULT_MODULES[plan] || [],
      billing_period: 'monthly',
      price_amount_xof: defaultMonthlyPrice(plan),
    }));
  };

  const toggleModule = (moduleCode) => {
    setForm((f) => {
      const next = new Set(f.enabled_modules || []);
      if (next.has(moduleCode)) next.delete(moduleCode);
      else next.add(moduleCode);
      return { ...f, enabled_modules: Array.from(next).sort() };
    });
  };

  // Ne se montrent que si la licence les porte déjà: le formulaire ne doit
  // jamais permettre d'en ajouter, seulement d'en retirer. Sans ce bloc, une
  // case héritée resterait cochée en base sans apparaître nulle part à
  // l'écran, donc impossible à corriger.
  const retiredStillEnabled = RETIRED_MODULES.filter(([code]) =>
    (form.enabled_modules || []).includes(code),
  );

  const criticalModules = ['workflow_engine', 'documents', 'stock_movements', 'volume_reconciliation', 'alerts'];
  const enabledCritical = criticalModules.filter((code) => (form.enabled_modules || []).includes(code));
  const selectedOrg = orgs.find((org) => org.id === form.organization_id);
  const orgOptions = useMemo(
    () =>
      orgs.map(organizationOption),
    [orgs],
  );

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      enabled_modules: Array.from(new Set(form.enabled_modules || [])).sort(),
      price_amount_xof: Number(form.price_amount_xof || 0),
      price_monthly_xof: monthlyEquivalent,
      price_annual_xof: annualEquivalent,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    try {
      if (license) {
        const { organization_id, ...patch } = payload;
        await licensesAPI.update(license.id, patch);
        toast.success('Licence mise à jour');
      } else {
        await licensesAPI.create(payload);
        toast.success('Licence créée');
      }
      onSaved();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible d’enregistrer la licence'));
    }
  };

  return (
    <Modal onClose={onClose} title={license ? 'Modifier la licence' : 'Nouvelle licence'}>
      <form onSubmit={submit} className="space-y-4">
        {isLegacyEdit && (
          <div className="rounded-lg border border-flame/30 bg-flame/10 p-3 text-sm text-flame-ink">
            Cette licence utilise une ancienne formule ({libelle('licensePlan', license.plan)}). Toute sauvegarde la basculera vers une formule actuelle.
          </div>
        )}
        {!license && (
          <Field label="Organisation">
            <SearchableSelect
              className="h-10"
              aria-label="Organisation"
              placeholder="Choisir une organisation"
              searchPlaceholder="Rechercher une organisation…"
              emptyMessage="Aucune organisation trouvée"
              value={form.organization_id}
              onValueChange={(organizationId) => setForm({ ...form, organization_id: organizationId })}
              options={orgOptions}
              required
            />
          </Field>
        )}
        <section className="rounded-xl border border-border bg-card p-3">
          <div className="mb-3 flex items-center gap-1">
            <p className="text-sm font-bold text-foreground">Choix de la formule</p>
            <Aide titre="Choix de la formule">La formule propose une sélection d’accès de départ; ce sont les accès cochés qui décident de ce que le client peut utiliser.</Aide>
          </div>
          <div className="grid gap-2 md:grid-cols-3">
            {PLANS.map((p) => (
              <button
                type="button"
                key={p}
                onClick={() => onPlanChange(p)}
                className={`rounded-lg border px-3 py-3 text-left text-sm ${
                  form.plan === p ? `ring-2 ring-process/60 ${planColor(p).bg} ${planColor(p).text}` : 'border-border text-muted-foreground'
                }`}
              >
                <span className="block font-black">{planNom(p)}</span>
                <span className="mt-1 block text-xs font-medium opacity-80">{PLAN_DESCRIPTION[p]}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-3">
          <div className="mb-3 flex items-center gap-1">
            <p className="text-sm font-bold text-foreground">Accès activés</p>
            <Aide titre="Accès activés">Décochez un accès pour retirer au client la page correspondante.</Aide>
          </div>
          <div className="space-y-4 rounded-lg border border-border bg-muted/50 p-3">
            {MODULE_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </div>
                <p className="mb-2 text-xs text-muted-foreground">{group.description}</p>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {group.modules.map(([code, label, help]) => {
                    const checked = (form.enabled_modules || []).includes(code);
                    return (
                      <label
                        key={code}
                        className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 transition-colors ${
                          checked
                            ? 'border-success/40 bg-success/10'
                            : 'border-border bg-card hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleModule(code)}
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border text-success"
                        />
                        <span className="min-w-0">
                          <span className="block text-xs font-semibold text-foreground">{label}</span>
                          {help && (
                            <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                              {help}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {retiredStillEnabled.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  Retirés du catalogue
                  <Aide titre="Retirés du catalogue">
                    Ces accès ont été vendus mais n’ouvrent aucun écran. Décochez-les :
                    ils ne peuvent plus être remis.
                  </Aide>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {retiredStillEnabled.map(([code, label, help]) => (
                    <label
                      key={code}
                      className="flex cursor-pointer items-start gap-2.5 rounded-md border border-flame/30 bg-flame/10 p-2.5"
                    >
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleModule(code)}
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border"
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-foreground">{label}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-flame-ink">{help}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="text-xs font-medium text-muted-foreground">
              {formatNombre((form.enabled_modules || []).length)} accès activés
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-3">
          <div className="mb-3 flex items-center gap-1">
            <p className="text-sm font-bold text-foreground">Limites</p>
            <Aide titre="Limites">Ces limites fixent le nombre d’utilisateurs et de camions autorisés pour cette organisation.</Aide>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
          <Field label="Utilisateurs maximum">
            <input type="number" min="1" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value, 10) })} className="w-full rounded-md border border-border px-3 py-2 text-sm" />
          </Field>
          <Field label="Camions maximum">
            <input type="number" min="0" value={form.max_trucks} onChange={(e) => setForm({ ...form, max_trucks: parseInt(e.target.value, 10) })} className="w-full rounded-md border border-border px-3 py-2 text-sm" />
          </Field>
          <Field label="Date d’expiration (facultative)">
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full rounded-md border border-border px-3 py-2 text-sm" />
          </Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-md border border-border px-3 py-2 text-sm" />
          </Field>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-3">
          <div className="mb-3 flex items-center gap-1">
            <p className="text-sm font-bold text-foreground">Prix de la licence</p>
            <Aide titre="Prix de la licence">Le prix saisi ici sert de base à la facture de cette organisation.</Aide>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Facturation">
              <select
                value={form.billing_period}
                onChange={(e) => setForm({ ...form, billing_period: e.target.value })}
                className="w-full rounded-md border border-border px-3 py-2 text-sm"
              >
                <option value="monthly">Chaque mois</option>
                <option value="annual">Chaque année</option>
              </select>
            </Field>
            <Field label={form.billing_period === 'annual' ? 'Prix annuel (FCFA)' : 'Prix mensuel (FCFA)'}>
              <input
                type="number"
                min="0"
                value={form.price_amount_xof}
                onChange={(e) => setForm({ ...form, price_amount_xof: e.target.value })}
                className="w-full rounded-md border border-border px-3 py-2 text-sm"
              />
            </Field>
            <div className="rounded-lg border border-process/30 bg-process/10 p-3 text-sm">
              <p className="text-xs font-bold uppercase text-process">Équivalent</p>
              <p className="mt-1 font-black text-foreground">{fmtXOF(monthlyEquivalent)} par mois</p>
              <p className="text-xs font-semibold text-muted-foreground">{fmtXOF(annualEquivalent)} par an</p>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-success/30 bg-success/10 p-3 text-sm">
          <p className="font-bold text-success">Récapitulatif</p>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            <div>
              <span className="text-xs font-semibold uppercase text-success">Client</span>
              <p className="font-medium text-foreground">{nomLisible(selectedOrg?.name, 'Organisation choisie')}</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Formule</span>
              <p className="font-medium text-foreground">{planNom(form.plan)}</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Accès ouverts</span>
              <p className="font-medium text-foreground">{formatNombre((form.enabled_modules || []).length)} accès</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Limites</span>
              <p className="font-medium text-foreground">{formatNombre(form.max_users || 0)} utilisateurs · {formatNombre(form.max_trucks || 0)} camions</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Prix de la licence</span>
              <p className="font-medium text-foreground">{fmtXOF(monthlyEquivalent)} par mois · {fmtXOF(annualEquivalent)} par an</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Expire le</span>
              <p className="font-medium text-foreground">{form.expires_at ? fmtDate(form.expires_at) : 'Sans date limite'}</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-success">Accès essentiels</span>
              <p className="font-medium text-foreground">{enabledCritical.map(moduleLabel).join(', ') || 'Aucun accès essentiel coché'}</p>
            </div>
          </div>
        </section>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm">Annuler</button>
          <button type="submit" className="rounded-md bg-process px-4 py-2 text-sm font-bold text-white hover:bg-process">
            {license ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LicenseDetailModal({ license, org, audit, onClose, onInvoice }) {
  return (
    <Modal onClose={onClose} title={`Licence ${planNom(license.plan)}`}>
      <div className="space-y-4">
        <div className="rounded-xl border border-process/30 bg-gradient-to-br from-process/10 to-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-process">Clé de licence</div>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(license.key);
                    toast.success('Clé de licence copiée');
                  } catch (e) {
                    toast.error('Impossible de copier la clé');
                  }
                }}
                className="mt-1 text-sm font-bold text-process hover:underline"
              >
                Copier la clé de licence
              </button>
            </div>
            <Key className="h-8 w-8 text-process" aria-hidden="true" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <KV label="Organisation" value={nomLisible(org?.name, '—')} />
          <KV label="Formule" value={planNom(license.plan)} />
          <KV label="État" value={libelle('licenseStatus', license.status)} />
          <KV label="Utilisateurs maximum" value={license.max_users} />
          <KV label="Camions maximum" value={license.max_trucks} />
          <KV label="Prix par mois" value={fmtXOF(license.price_monthly_xof ?? defaultMonthlyPrice(license.plan))} />
          <KV label="Prix par an" value={fmtXOF(license.price_annual_xof ?? defaultMonthlyPrice(license.plan) * 12)} />
          <KV label="Créée le" value={fmtDateTime(license.created_at)} />
          <KV label="Émise le" value={fmtDateTime(license.issued_at)} />
          <KV label="Expire le" value={license.expires_at ? formatDate(license.expires_at) : 'Jamais'} />
        </div>

        <div className="rounded-xl border border-border bg-card p-3">
          <h4 className="mb-2 readout-label">
            Accès activés
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {(license.enabled_modules || DEFAULT_MODULES[license.plan] || []).map((moduleCode) => (
              <span
                key={moduleCode}
                className="rounded-md bg-muted px-2 py-1 text-[11px] font-semibold text-muted-foreground"
              >
                {moduleLabel(moduleCode)}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-muted/50 p-3">
          <div className="mb-2 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-success" />
            <h4 className="text-sm font-bold text-foreground">Facturation</h4>
          </div>
          <div className="flex gap-2">
            {[1, 3, 6, 12, 24].map((m) => (
              <button
                key={m}
                onClick={() => onInvoice(m)}
                className="inline-flex items-center gap-1 rounded-md border border-process/30 bg-card px-2 py-1 text-xs font-bold text-process hover:bg-process/10"
              >
                <Download className="h-3 w-3" aria-hidden="true" /> {m} mois
              </button>
            ))}
          </div>
        </div>

        <div>
          <h4 className="mb-2 readout-label">Historique</h4>
          <div className="max-h-64 space-y-1 overflow-auto">
            {audit.length === 0 && <div className="text-sm text-muted-foreground/70">Aucun historique pour le moment</div>}
            {audit.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-md border border-border/60 bg-card p-2 text-xs">
                <div>
                  <span className="inline-block rounded bg-readout px-1.5 py-0.5 text-[10px] font-bold text-white">{libelle('licenseAudit', a.action)}</span>
                  <span className="ml-2 text-muted-foreground">{nomLisible(a.performed_by_email, a.performed_by, 'Système')}</span>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground/70">{fmtDateTime(a.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-2xl rounded-2xl bg-card shadow-2xl">
      <div className="flex items-center justify-between border-b border-border p-4">
        <h3>{title}</h3>
        <button onClick={onClose} className="rounded-full p-1 hover:bg-muted">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="max-h-[80vh] overflow-y-auto p-5">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div className="rounded-md border border-border/60 bg-muted/50 p-2">
    <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-0.5 text-sm font-semibold text-foreground">{value === 0 ? 0 : value || '—'}</div>
  </div>
);
